/**
 * Whether a stored document is done, as the Study Documents cards and the
 * project Documents list show it (docgen reasons, round 3, 2026-10-01).
 *
 * A document with a section that could not be generated is INCOMPLETE: it is
 * still listed and still opens, but it is never shown as Ready or as done.
 * The generators record it as:
 *   - generationStatus "incomplete" / failedSectionCount > 0 (EDTS, MCS, SDTM)
 *   - failedSectionIds non-empty (DMP)
 *   - artifact status "incomplete" (EDTS, MCS, SDTM artifact records)
 */

type DocLike = {
  generationStatus?: unknown;
  failedSectionCount?: unknown;
  failedSectionIds?: unknown;
  sections?: unknown;
} | null | undefined;

/**
 * Section statuses that mean the section is done (round 5, item 4). Also
 * "not_applicable": EDTS stores it for a section that does not apply to the
 * study (edts/edtsSectionRecords.ts), which is not a missing section.
 */
const GOOD_SECTION = new Set(['generated', 'edited', 'reviewed', 'approved', 'final', 'not_applicable']);

/**
 * Per-section status wins when the document carries it (round 4, D1): it is
 * what the user just changed by regenerating or editing a section, while a
 * stored summary (failedSectionIds, generationStatus) can be stale.
 *
 * Round 5, item 4: any non-empty section status outside the good set
 * (generation_failed, failed, or anything unknown) makes the document
 * incomplete. Only when no section carries a status at all does the stored
 * summary decide, so generationStatus "incomplete" still counts on a
 * partly-populated document.
 */
export function isDocumentIncomplete(doc: DocLike | Record<string, unknown>): boolean {
  const d = (doc ?? {}) as Exclude<DocLike, null | undefined>;
  const sections = Array.isArray(d.sections) ? (d.sections as Array<{ status?: unknown }>) : [];
  const withStatus = sections.filter((s) => typeof s?.status === 'string' && s.status !== '');
  if (withStatus.some((s) => !GOOD_SECTION.has(String(s.status)))) return true;
  if (withStatus.length) return false;
  if (d.generationStatus === 'incomplete') return true;
  if (typeof d.failedSectionCount === 'number' && d.failedSectionCount > 0) return true;
  if (Array.isArray(d.failedSectionIds) && d.failedSectionIds.length > 0) return true;
  return false;
}

export type HubBadge = { kind: 'ready' | 'incomplete' | 'generating'; label: string };

/** The badge on a Study Documents card. */
export function hubBadge(status: string, doc: DocLike | Record<string, unknown>): HubBadge | null {
  if (status === 'generating') return { kind: 'generating', label: 'Generating' };
  if (status !== 'generated') return null;
  return isDocumentIncomplete(doc) ? { kind: 'incomplete', label: 'Incomplete' } : { kind: 'ready', label: 'Ready' };
}

const ARTIFACT_LABELS: Record<string, string> = {
  generated: 'Generated',
  approved: 'Approved',
  incomplete: 'Incomplete',
};

/**
 * How the project Documents list shows an artifact: generated and approved
 * are listed and done; incomplete is listed, labelled, and not done; other
 * statuses (drafts, in progress) are not listed.
 */
export function artifactListStatus(status: string): { listed: boolean; label: string; done: boolean } {
  const s = String(status ?? '');
  const label = ARTIFACT_LABELS[s];
  if (!label) return { listed: false, label: s.replace(/_/g, ' '), done: false };
  return { listed: true, label, done: s !== 'incomplete' };
}
